import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";
import { PESTEL_ROWS, SCOPES } from "@/components/blueprints/LongPestelBlueprint";

const STEPS = PESTEL_ROWS.map((r, i) => ({ n: i + 1, code: String(i + 1), key: r.key, label: r.title, phase: "STRATEGY" }));
const LAST = STEPS.length;
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

export default function LongPestel() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData(res.data.payload || {}); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled PESTEL Analysis", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const cell = data[cur.key] || {};
  const setScope = (scope, v) => setData((d) => ({ ...d, [cur.key]: { ...(d[cur.key] || {}), [scope]: v } }));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Analysis finalised"); navigate(`/pestel/${id}/view`); } };
  const applySuggestion = (text) => { setScope("global", !cell.global?.trim() ? text : `${cell.global.trimEnd()}\n- ${text}`); toast.success("Added to Global"); };
  const onPull = ({ title: t, context }) => { if (context) setData((d) => ({ ...d, political: { ...(d.political || {}), local: context } })); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="pestel-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="PESTEL analysis title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="pestel-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)} className={`rounded-sm border px-2 py-2.5 text-center transition-all ${step === s.n ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold block ${step === s.n ? "text-indigo-700" : "text-slate-400"}`}>{s.label[0]}</span>
              <p className={`text-[10px] font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>
        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label} — across horizons</h2>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {SCOPES.map((sc) => (
              <div key={sc.key}>
                <label className="block text-xs font-semibold tracking-[0.08em] uppercase text-indigo-700 mb-1.5">{sc.title}</label>
                <textarea data-testid={`field-${cur.key}-${sc.key}`} rows={7} className={taCls} value={cell[sc.key] || ""} onChange={(e) => setScope(sc.key, e.target.value)} placeholder={`${cur.label} factors at the ${sc.title.toLowerCase()} level…`} />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="pestel-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="pestel-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="pestel-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">Next Vector <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="pestel-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Analysis</button>
            )}
          </div>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="long_pestel" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
